// A restricted session (docs/SECURITY.md): before anything else the user sets a new password (after
// a temporary one) and/or switches on two-factor authentication (when the firm requires it).
import { useEffect, useRef, useState } from 'react';
import type { User } from '../../../shared/types';
import { authChanged } from '../lib/api';
import { changePassword } from '../lib/account';
import { patchFeatures } from '../lib/hooks';
import { noteUnrestricted } from '../lib/session';
import { accountErrorText, FormError, NewPasswordFields, newPasswordProblem, PasswordInput } from '../components/AccountForms';
import { Icon } from '../components/Icon';
import { confirmAndLogout } from '../components/Logout';
import { showRecoveryCodes } from '../components/RecoveryCodes';
import { toast } from '../components/Toasts';
import { TotpSetupForm } from '../components/TotpSetup';

function SignedInAs({ user }: { user: User }) {
  const [busy, setBusy] = useState(false);
  const out = async () => {
    setBusy(true);
    try {
      await confirmAndLogout();
    } finally {
      setBusy(false);
    }
  };
  return (
    <p className="muted small signed-in-as">
      Prijavljeni ste kot <strong>{user.displayName}</strong> ({user.username}).{' '}
      <button type="button" className="btn btn--link btn--small" onClick={() => void out()} disabled={busy}>
        Odjava
      </button>
    </p>
  );
}

function ForcedPassword({ user }: { user: User }) {
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);
  useEffect(() => firstRef.current?.focus(), []);

  const problem = !current ? 'Vpišite trenutno (začasno) geslo.' : newPasswordProblem(password, repeat, user.username);

  const submit = async () => {
    setTried(true);
    if (problem || busy) return;
    setBusy(true);
    setError(null);
    try {
      await changePassword({ currentPassword: current, newPassword: password });
      setCurrent('');
      setPassword('');
      setRepeat('');
      toast('Geslo je spremenjeno.', { kind: 'success' });
    } catch (err) {
      setError(accountErrorText(err, 'Gesla ni bilo mogoče spremeniti.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="card login login--wide"
      noValidate
      aria-labelledby="forced-password-title"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <span className="login__icon" aria-hidden="true">
        <Icon name="lock" size={28} />
      </span>
      <h1 id="forced-password-title" className="login__title">
        Nastavite novo geslo
      </h1>
      <p className="muted">Prijavili ste se z začasnim geslom. Izberite svoje geslo, ki ga poznate samo vi.</p>
      <PasswordInput
        id="forced-current"
        label="Trenutno (začasno) geslo"
        value={current}
        onChange={setCurrent}
        autoComplete="current-password"
        inputRef={firstRef}
        enterKeyHint="next"
        disabled={busy}
      />
      <NewPasswordFields
        idPrefix="forced"
        password={password}
        repeat={repeat}
        onPassword={setPassword}
        onRepeat={setRepeat}
        username={user.username}
        disabled={busy}
      />
      {error ? <FormError>{error}</FormError> : tried && problem ? <FormError>{problem}</FormError> : null}
      <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
        <Icon name={busy ? 'spinner' : 'check'} size={20} /> Shrani geslo
      </button>
      <SignedInAs user={user} />
    </form>
  );
}

/**
 * Ends the forced flow once the recovery codes were acknowledged (App shows them, see
 * usePendingRecoveryCodes: enabling 2FA lifts the restriction, the codes must stay on screen).
 */
export function finishTotpSetup(user: User): void {
  // The server lifted the restriction already: do not show the setup again while re-reading.
  patchFeatures({ restricted: false, user: { ...user, totpEnabled: true } });
  noteUnrestricted(user.id);
  authChanged();
}

function ForcedTotp({ user }: { user: User }) {
  return (
    <section className="card login login--wide" aria-labelledby="forced-totp-title">
      <span className="login__icon" aria-hidden="true">
        <Icon name="lock" size={28} />
      </span>
      <h1 id="forced-totp-title" className="login__title">
        Vklopite dvostopenjsko prijavo
      </h1>
      <p className="muted">
        Za dostop do posnetkov in zapisnikov je poleg gesla obvezna še koda iz aplikacije na vašem telefonu. Tudi če kdo izve vaše geslo, se
        brez telefona ne more prijaviti.
      </p>
      <TotpSetupForm onEnabled={(codes) => showRecoveryCodes(codes, user.username, user.id)} />
      <SignedInAs user={user} />
    </section>
  );
}

/** The screen a restricted session shows instead of the app. */
export function AccountSetupPage({ user }: { user: User }) {
  return <div className="page page--center page--login">{user.mustChangePassword ? <ForcedPassword user={user} /> : <ForcedTotp user={user} />}</div>;
}
